// The "Fields" block of the calculation step (phase M2c): a magnetic field B (orbital: Peierls phases; spin and
// orbital Zeeman terms) and a transverse electric field E, for every calculation; the |B| sweep of T(B, E).
import { Checkbox, Group, NumberInput, Select, Stack, Switch, Text } from "@mantine/core";
import { defaultField, defaultFieldSweep, fieldActive, SWEEP_DIRECTIONS, type FieldForm, type FieldSweepForm,
  type SweepDirection } from "../../lib/fields";

function N({ label, value, onChange, unit, step, int = false, min }: {
  label: string; value: number; onChange: (v: number) => void; unit?: string; step?: number; int?: boolean; min?: number;
}) {
  return <NumberInput label={label} size="xs" value={value} step={step} min={min} allowDecimal={!int} decimalScale={int ? 0 : 6}
    rightSection={unit ? <Text size="xs" c="dimmed">{unit}</Text> : undefined} rightSectionWidth={unit ? 40 : undefined}
    onChange={(v) => onChange(typeof v === "number" ? v : Number(v) || 0)} />;
}

function Vec({ label, value, onChange, unit, step }: { label: string; value: number[]; onChange: (v: number[]) => void; unit: string; step: number }) {
  return (
    <Group grow gap="xs" align="flex-end">
      {["x", "y", "z"].map((a, i) => (
        <N key={a} label={`${label}${a}`} unit={unit} step={step} value={value[i] ?? 0}
          onChange={(v) => onChange(value.map((x, j) => (j === i ? v : x)))} />
      ))}
    </Group>
  );
}

export function FieldsBlock({ field, sweep, onField, onSweep, isSweep, spinful, maxFieldPoints }: {
  field: FieldForm | undefined; sweep: FieldSweepForm | undefined; onField: (f: FieldForm | undefined) => void;
  onSweep: (s: FieldSweepForm) => void; isSweep: boolean; spinful: boolean; maxFieldPoints?: unknown;
}) {
  const f = field ?? defaultField();
  const on = isSweep || !!field;
  const sw = sweep ?? defaultFieldSweep();
  const set = (next: Partial<FieldForm>) => onField({ ...f, ...next });
  return (
    <Stack gap={6} data-testid="fields-block">
      {!isSweep && (
        <Switch label="Magnetic / electric field" checked={on} onChange={(e) => onField(e.currentTarget.checked ? f : undefined)} />
      )}
      {on && (
        <>
          {isSweep ? (
            <>
              <Group grow gap="xs">
                <N label="|B| from" unit="T" value={sw.B_min} onChange={(v) => onSweep({ ...sw, B_min: v })} />
                <N label="to" unit="T" value={sw.B_max} onChange={(v) => onSweep({ ...sw, B_max: v })} />
                <N label="fields" int min={2} value={sw.n} onChange={(v) => onSweep({ ...sw, n: v })} />
              </Group>
              <Select size="xs" label="Direction of B" data={SWEEP_DIRECTIONS} value={sw.direction} allowDeselect={false}
                onChange={(v) => onSweep({ ...sw, direction: (v ?? "out-of-plane") as SweepDirection })} />
              <Text size="xs" c="dimmed">One device per field strength. Your plan: up to {String(maxFieldPoints ?? "?")} fields.</Text>
            </>
          ) : (
            <Vec label="B" unit="T" step={1} value={f.B} onChange={(B) => set({ B })} />
          )}
          <Vec label="E" unit="V/Å" step={0.001} value={f.E} onChange={(E) => set({ E })} />
          <Group gap="md">
            <Checkbox size="xs" label="Orbital (Peierls)" checked={f.peierls} onChange={(e) => set({ peierls: e.currentTarget.checked })} />
            <Checkbox size="xs" label="Zeeman" checked={f.zeeman} onChange={(e) => set({ zeeman: e.currentTarget.checked })} />
            {f.zeeman && <Checkbox size="xs" label="orbital μB L·B" checked={f.orbital_zeeman}
              onChange={(e) => set({ orbital_zeeman: e.currentTarget.checked })} />}
          </Group>
          {f.zeeman && <N label="Spin g factor" step={0.1} value={f.g} onChange={(g) => set({ g })} />}
          <Text size="xs" c="dimmed">
            Orbital: B enters the hoppings as Peierls phases (Landau levels, quantum Hall edge states, Aharonov–Bohm). A flux
            quantum through a ribbon a few nm wide needs hundreds of tesla - use large fields in small devices.
            Zeeman: (g μB/2) B·σ{f.orbital_zeeman ? " and μB L·B" : ""}{!spinful && f.zeeman ? "; the model is made spinful for it" : ""}.
            E is across the transport direction (a bias is set by the I–V calculations).
          </Text>
          {!isSweep && field && !fieldActive(field) && <Text size="xs" c="orange">B and E are zero: no field is applied.</Text>}
        </>
      )}
    </Stack>
  );
}
